import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  CANNED_FIELD_METADATA,
  getIsAutoOnlyCannedVariant,
  getIsCannedValueClosed,
  getISO8601DateString,
  isCannedFieldElement,
  isDynamicBlockElement,
  isInputFieldElement,
  isValidEmail,
  NUMBER_FIELD_WARNING,
  OpCode,
  Select,
  shouldShowNumberFieldWarning,
} from '@aline/common';
import { FormControlLabel, RadioGroup, Typography } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import type { Range as SlateRange } from 'slate';
import { Editor, Range, Transforms } from 'slate';
import { AlineInput } from 'src/AlineInput';
import {
  AlineRadio,
  AnswerOptionsAutocomplete,
  clearVariablePanelFocusedField,
  setVariablePanelFocusedField,
  useUpdateOptionsVariableValue,
  useUpdatePrimitiveVariableValue,
  useVariablePanelFocusedField,
  VariablePanelStateProvider,
} from 'src/components';
import { useCannedFieldValueWrite } from 'src/components/variables/document-variables/use-canned-field-value-write';
import { useRequiredEditorRuntime } from 'src/editor/editor-runtime-registry';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { MOBILE_SHEET_PORTAL_CLASS, MobileBottomSheet } from 'src/editor/mobile/MobileBottomSheet';
import { mobileSheetAutoFocusVar } from 'src/editor/mobile/signing/mobile-sheet-autofocus';
import { MobileSheetActionFooter } from 'src/editor/mobile/signing/MobileSheetActionFooter';
import { SignatureFieldBody } from 'src/editor/mobile/signing/MobileSignatureFieldSheet';
import { useWalkthroughActive } from 'src/editor/toolbars';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useDocumentParams } from 'src/helpers';
import {
  useGetCurrentDoc,
  useGetDocumentFields,
  useOnChange,
  useWorkflowCannedFields,
} from 'src/hooks';
import { useEditor, useSelection } from 'src/layout-engine/hooks';
import { makeStyles } from 'tss-react/mui';

type DocumentVariableFromQuery = NonNullable<
  Apollo.GetDocumentFieldsQueryHookResult['data']
>['document']['variables'][number];

type SignatureFieldFromQuery = NonNullable<
  Apollo.GetDocumentFieldsQueryHookResult['data']
>['document']['signatureFields'][number];

type CannedFieldFromQuery = Apollo.CannedFieldFullFragment;

const useStyles = makeStyles()(theme => ({
  dateInput: {
    WebkitAppearance: 'none',
    appearance: 'none',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    boxSizing: 'border-box',
    color: theme.palette.text.primary,
    display: 'block',
    fontFamily: theme.typography.fontFamily,
    fontSize: 16,
    height: 44,
    lineHeight: '42px',
    minHeight: 0,
    paddingBlock: 0,
    paddingInline: theme.spacing(1.5),
    width: '100%',
  },
  label: {
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: theme.spacing(0.5),
  },
  question: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  radioGroup: {
    gap: theme.spacing(0.5),
  },
  radioOption: {
    margin: 0,
  },
}));

export function MobileSigningFieldSheet(): JSX.Element | null {
  const { walkThroughActive } = useWalkthroughActive();
  const { tabId } = useRequiredEditorRuntime();
  const focusedField = useVariablePanelFocusedField(tabId);
  const { documentId } = useDocumentParams();
  const { allSignatures, allVariables } = useGetDocumentFields(documentId);
  const { document } = useGetCurrentDoc(documentId);
  const { cannedById } = useWorkflowCannedFields(document?.workflow?.id ?? null);
  const editor = useEditor();
  const selection = useSelection();
  const lastMouseDownTimestamp = useEditorTabStateSelector(
    state => state.click.lastMouseDownTimestamp
  );

  const focusedFieldRef = useRef(focusedField);
  useEffect(() => {
    focusedFieldRef.current = focusedField;
  }, [focusedField]);

  const dismissedFieldIdRef = useRef<string | null>(null);
  const dismissedSelectionRef = useRef<SlateRange | null>(null);

  useOnChange(() => {
    dismissedFieldIdRef.current = null;
    dismissedSelectionRef.current = null;
  }, [lastMouseDownTimestamp]);
  useOnChange(() => {
    if (!walkThroughActive || !selection) return;
    if (!allVariables.length && !allSignatures.length && !cannedById.size) return;
    const dismissedSelection = dismissedSelectionRef.current;
    if (!dismissedSelection || !Range.equals(selection, dismissedSelection)) {
      dismissedFieldIdRef.current = null;
      dismissedSelectionRef.current = null;
    }

    const entry = Editor.above(editor, {
      at: selection,
      match: n => isInputFieldElement(n) || isDynamicBlockElement(n) || isCannedFieldElement(n),
    });
    if (!entry) return;
    const [node] = entry;
    const field = isCannedFieldElement(node)
      ? cannedById.get(node.cannedFieldId)
      : isInputFieldElement(node) && node.variant === Apollo.FieldVariant.Signature
        ? allSignatures.find(s => s.nodeId === node.fieldId)
        : allVariables.find(v => v.nodeId === node.fieldId);
    if (!field || !isFieldSupported(field)) return;
    const fieldNodeId = getFieldNodeId(field);
    if (dismissedFieldIdRef.current === fieldNodeId) return;
    if (focusedFieldRef.current?.nodeId === fieldNodeId) return;
    /**
     * Tapping a field in the document is a user gesture — auto-focus the
     * sheet's input so they can type straight away.
     */
    mobileSheetAutoFocusVar(true);
    setVariablePanelFocusedField(tabId, toFocusedField(field));
  }, [walkThroughActive, selection, editor, allVariables, allSignatures, cannedById, tabId]);

  /**
   * Signature fields are voids; an explicit `Transforms.deselect` is needed so
   * re-tapping the same void registers as a real selection change.
   */
  const dismissSheet = useCallback((): void => {
    const field = focusedFieldRef.current;
    if (field && getIsSignatureField(field)) {
      Transforms.deselect(editor);
    } else {
      dismissedFieldIdRef.current = field?.nodeId ?? null;
      dismissedSelectionRef.current = editor.selection;
    }
    clearVariablePanelFocusedField(tabId);
  }, [editor, tabId]);

  if (!walkThroughActive || !focusedField) return null;

  let body: JSX.Element | null = null;
  let bodyOwnsFooter = false;
  if (getIsSignatureField(focusedField)) {
    const signatureField = allSignatures.find(s => s.nodeId === focusedField.nodeId);
    if (signatureField && !signatureField.deletedAt) {
      bodyOwnsFooter = true;
      body = (
        <SignatureFieldBody
          key={signatureField.id}
          signatureField={signatureField}
          onDismiss={dismissSheet}
        />
      );
    }
  } else if (getIsCannedField(focusedField)) {
    const cannedField = cannedById.get(focusedField.nodeId);
    if (cannedField && !cannedField.deletedAt) {
      body = <CannedFieldBody key={cannedField.id} cannedField={cannedField} />;
    }
  } else {
    const variable = allVariables.find(v => v.nodeId === focusedField.nodeId);
    if (variable && isVariableSupported(variable)) {
      body = renderVariableBody(variable);
    }
  }

  if (!body) return null;

  return (
    <VariablePanelStateProvider>
      <SheetWrapper dismiss={dismissSheet} noDefaultFooter={bodyOwnsFooter}>
        {body}
      </SheetWrapper>
    </VariablePanelStateProvider>
  );
}

function SheetWrapper({
  children,
  dismiss,
  noDefaultFooter,
}: {
  children: React.ReactNode;
  dismiss: () => void;
  noDefaultFooter?: boolean;
}): JSX.Element {
  return (
    <MobileBottomSheet onDismiss={dismiss}>
      {children}
      {!noDefaultFooter && <MobileSheetActionFooter onDismiss={dismiss} />}
    </MobileBottomSheet>
  );
}

type VariableBodyComponent = React.ComponentType<{ variable: DocumentVariableFromQuery }>;

/**
 * Single source of truth for which variants the mobile sheet handles.
 * `isVariableSupported` reads from the same map so adding a new variant here
 * automatically opts it in to the selection watcher.
 */
const VARIANT_BODIES: Partial<Record<Apollo.MetadataVariant, VariableBodyComponent>> = {
  [Apollo.MetadataVariant.Currency]: NumberFieldBody,
  [Apollo.MetadataVariant.Date]: DateFieldBody,
  [Apollo.MetadataVariant.Decimal]: NumberFieldBody,
  [Apollo.MetadataVariant.Email]: TextFieldBody,
  [Apollo.MetadataVariant.Options]: OptionsFieldBody,
  [Apollo.MetadataVariant.RadioOptions]: RadioOptionsFieldBody,
  [Apollo.MetadataVariant.Text]: TextFieldBody,
};

function renderVariableBody(variable: DocumentVariableFromQuery): JSX.Element | null {
  const Component = VARIANT_BODIES[variable.variant];
  if (!Component) return null;
  return <Component key={variable.id} variable={variable} />;
}

function getIsSignatureField<T extends { __typename: string }>(
  field: T
): field is Extract<T, { __typename: 'SignatureField' }> {
  return field.__typename === 'SignatureField';
}

function getIsCannedField<T extends { __typename: string }>(
  field: T
): field is Extract<T, { __typename: 'CannedField' }> {
  return field.__typename === 'CannedField';
}

function isFieldSupported(
  field:
    | Pick<DocumentVariableFromQuery, '__typename' | 'variant'>
    | Pick<SignatureFieldFromQuery, '__typename'>
    | Pick<CannedFieldFromQuery, '__typename'>
): boolean {
  if (getIsSignatureField(field)) return true;
  if (getIsCannedField(field)) return true;
  return isVariableSupported(field);
}

function isVariableSupported(variable: Pick<DocumentVariableFromQuery, 'variant'>): boolean {
  return variable.variant in VARIANT_BODIES;
}

function getFieldNodeId(
  field: DocumentVariableFromQuery | SignatureFieldFromQuery | CannedFieldFromQuery
): UUID {
  return field.nodeId;
}

function toFocusedField(
  field: DocumentVariableFromQuery | SignatureFieldFromQuery | CannedFieldFromQuery
) {
  if (getIsCannedField(field)) {
    return { __typename: 'CannedField' as const, id: field.id, nodeId: field.nodeId };
  }
  return field;
}

function FieldBodyShell({
  children,
  variable,
}: {
  children: React.ReactNode;
  variable: DocumentVariableFromQuery;
}): JSX.Element {
  const { classes } = useStyles();
  return (
    <>
      <Typography className={classes.label} variant="subtitle2">
        {variable.label}
      </Typography>
      {variable.question && (
        <Typography className={classes.question} variant="body2">
          {variable.question}
        </Typography>
      )}
      {children}
    </>
  );
}

function TextFieldBody({ variable }: { variable: DocumentVariableFromQuery }): JSX.Element {
  const [shouldAutoFocus] = useState(() => mobileSheetAutoFocusVar());
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState<string>(variable.value ?? '');
  const { resetOnBlur, updatePrimitiveVariable } = useUpdatePrimitiveVariableValue(variable, {
    isFocused,
    syncVariableValue: v => setValue(v.value ?? ''),
  });

  return (
    <FieldBodyShell variable={variable}>
      <AlineInput
        multiline
        autoFocus={shouldAutoFocus}
        inputProps={{ style: { fontSize: 16 } }}
        placeholder="Enter text..."
        value={value}
        onBlur={() => {
          const opCode = resetOnBlur();
          if (opCode === OpCode.Success) setValue('');
          setIsFocused(false);
        }}
        onChange={async e => {
          setValue(e.target.value);
          await updatePrimitiveVariable({
            formattedValue: e.target.value,
            value: e.target.value,
          });
        }}
        onFocus={e => {
          e.target.select();
          setIsFocused(true);
        }}
      />
    </FieldBodyShell>
  );
}

function DateFieldBody({ variable }: { variable: DocumentVariableFromQuery }): JSX.Element {
  const { classes } = useStyles();
  const [shouldAutoFocus] = useState(() => mobileSheetAutoFocusVar());
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState<string>(variable.value ?? '');
  const { resetOnBlur, updatePrimitiveVariable } = useUpdatePrimitiveVariableValue(variable, {
    isFocused,
    syncVariableValue: v => setValue(v.value ?? ''),
  });

  return (
    <FieldBodyShell variable={variable}>
      <input
        autoFocus={shouldAutoFocus}
        className={classes.dateInput}
        type="date"
        value={value}
        onBlur={() => {
          const opCode = resetOnBlur();
          if (opCode === OpCode.Success) setValue('');
          setIsFocused(false);
        }}
        onChange={async e => {
          const dateStr = e.target.value;
          setValue(dateStr);
          const date = dateStr ? parseISO(dateStr) : null;
          await updatePrimitiveVariable({
            formattedValue: date && isValid(date) ? format(date, 'MMMM d, yyyy') : null,
            value: date && isValid(date) ? getISO8601DateString(date) : null,
          });
        }}
        onFocus={() => setIsFocused(true)}
      />
    </FieldBodyShell>
  );
}

function NumberFieldBody({ variable }: { variable: DocumentVariableFromQuery }): JSX.Element {
  const [shouldAutoFocus] = useState(() => mobileSheetAutoFocusVar());
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState<string>(variable.value ?? '');
  const { resetOnBlur, updatePrimitiveVariable } = useUpdatePrimitiveVariableValue(variable, {
    isFocused,
    syncVariableValue: v => setValue(v.value ?? ''),
  });

  return (
    <FieldBodyShell variable={variable}>
      <AlineInput
        autoFocus={shouldAutoFocus}
        helperText={NUMBER_FIELD_WARNING}
        hideHelperText={!shouldShowNumberFieldWarning(value)}
        inputMode="decimal"
        inputProps={{ inputMode: 'decimal', style: { fontSize: 16 } }}
        placeholder="Enter number..."
        value={value}
        onBlur={() => {
          const opCode = resetOnBlur();
          if (opCode === OpCode.Success) setValue('');
          setIsFocused(false);
        }}
        onChange={async e => {
          const nextValue = e.target.value;
          setValue(nextValue);
          await updatePrimitiveVariable({
            formattedValue: nextValue,
            value: nextValue,
          });
        }}
        onFocus={e => {
          e.target.select();
          setIsFocused(true);
        }}
      />
    </FieldBodyShell>
  );
}

function OptionsFieldBody({ variable }: { variable: DocumentVariableFromQuery }): JSX.Element {
  const [shouldAutoFocus] = useState(() => mobileSheetAutoFocusVar());
  const [isFocused, setIsFocused] = useState(false);
  const { options, resetOnBlur, selectedAnswer, setSelectedAnswer, updateOptionsVariable } =
    useUpdateOptionsVariableValue(variable, { isFocused });

  return (
    <FieldBodyShell variable={variable}>
      <AnswerOptionsAutocomplete
        openOnFocus
        autoFocus={shouldAutoFocus}
        options={options}
        placeholder="Select option..."
        slotProps={{
          listbox: {
            sx: {
              maxHeight: 232,
              ...(options.length > 4 && {
                WebkitMaskImage: 'linear-gradient(to bottom, #000 calc(100% - 32px), transparent)',
                maskImage: 'linear-gradient(to bottom, #000 calc(100% - 32px), transparent)',
              }),
            },
          },
          /**
           * The listbox is portal'd outside the sheet so it isn't clipped by
           * the sheet's own overflow; the marker class lets MobileBottomSheet's
           * ClickAwayListener ignore taps that land inside it. zIndex is bumped
           * one tick above the sheet (which sits at theme.zIndex.modal) so the
           * listbox renders on top instead of behind it.
           */
          popper: {
            className: MOBILE_SHEET_PORTAL_CLASS,
            placement: 'top-start',
            sx: theme => ({ zIndex: theme.zIndex.modal + 1 }),
          },
        }}
        // sx bumps the inner input font-size to 16 so iOS Safari doesn't auto-zoom on focus.
        sx={{ '& .MuiInputBase-input': { fontSize: 16 } }}
        value={selectedAnswer}
        onBlur={() => {
          const opCode = resetOnBlur();
          if (opCode === OpCode.Success) setSelectedAnswer(null);
          setIsFocused(false);
        }}
        onChange={async (_event, value) => {
          await updateOptionsVariable(value);
        }}
        onFocus={() => setIsFocused(true)}
      />
    </FieldBodyShell>
  );
}

function RadioOptionsFieldBody({ variable }: { variable: DocumentVariableFromQuery }): JSX.Element {
  const { classes } = useStyles();
  const { options, selectedAnswer, updateOptionsVariable } = useUpdateOptionsVariableValue(
    variable,
    { isFocused: false }
  );

  return (
    <FieldBodyShell variable={variable}>
      <RadioGroup
        className={classes.radioGroup}
        sx={{
          flexWrap: 'nowrap',
          maxHeight: 150,
          overflowY: 'auto',
          ...(options.length > 3 && {
            WebkitMaskImage: 'linear-gradient(to bottom, #000 calc(100% - 32px), transparent)',
            maskImage: 'linear-gradient(to bottom, #000 calc(100% - 32px), transparent)',
          }),
        }}
        value={selectedAnswer?.id ?? ''}
        onChange={async e => {
          const next = options.find(o => o.id === e.target.value) ?? null;
          await updateOptionsVariable(next);
        }}
      >
        {options.map(option => (
          <FormControlLabel
            key={option.id}
            className={classes.radioOption}
            control={<AlineRadio checked={option.id === selectedAnswer?.id} size="small" />}
            label={option.optionText}
            value={option.id}
          />
        ))}
      </RadioGroup>
    </FieldBodyShell>
  );
}

function CannedFieldBody({ cannedField }: { cannedField: CannedFieldFromQuery }): JSX.Element {
  const { classes } = useStyles();
  const currentUserId = useCurrentUserId();
  const [shouldAutoFocus] = useState(() => mobileSheetAutoFocusVar());
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState<string>(cannedField.value ?? '');
  const meta = CANNED_FIELD_METADATA[cannedField.variant];
  const isAutoOnly = getIsAutoOnlyCannedVariant(cannedField.variant);
  const isAssignedToCurrentUser =
    cannedField.assignee?.assignee?.id != null &&
    cannedField.assignee.assignee.id === currentUserId;
  const isReadOnly =
    isAutoOnly || !isAssignedToCurrentUser || getIsCannedValueClosed(cannedField.assignee?.status);
  const helperText = meta.buildTooltip(cannedField.value ?? cannedField.defaultValue);
  const isEmail = cannedField.variant === Apollo.CannedFieldVariant.UserEmail;
  const { flushWrite, writeValue } = useCannedFieldValueWrite({
    cannedFieldId: cannedField.id,
    isEmail,
    savedValue: cannedField.value,
    setValue,
    workflowId: cannedField.workflowId,
  });
  const emailError = isEmail && value !== '' && !isValidEmail(value);

  useEffect(() => {
    if (isFocused) return;
    setValue(cannedField.value ?? '');
  }, [cannedField.value, isFocused]);

  return (
    <>
      <Typography className={classes.label} variant="subtitle2">
        {meta.label}
      </Typography>
      <Typography className={classes.question} variant="body2">
        {helperText}
      </Typography>
      <AlineInput
        aria-label={meta.label}
        autoFocus={!isReadOnly && shouldAutoFocus}
        dataCy={Select.document.editor.rightDrawer.outline.cannedFieldRowInput}
        disabled={isReadOnly}
        error={emailError}
        helperText={emailError ? 'Please enter a valid email address.' : undefined}
        inputProps={{ style: { fontSize: 16 } }}
        placeholder={cannedField.defaultValue ?? ''}
        type={isEmail ? 'email' : 'text'}
        value={isReadOnly ? (cannedField.value ?? cannedField.defaultValue ?? '') : value}
        onBlur={() => {
          flushWrite();
          setIsFocused(false);
        }}
        onChange={e => writeValue(e.target.value)}
        onFocus={e => {
          e.target.select();
          setIsFocused(true);
        }}
      />
    </>
  );
}
